import { useEffect } from 'react';

const HEADER_STYLE = `
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  border-bottom: 1px solid hsl(var(--border));
  background-color: hsl(var(--background));
  white-space: nowrap;
  overflow: hidden;
`;

const ICON_CONTAINER_STYLE = `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const CLONED_ICON_STYLE = `
  width: 20px;
  height: 20px;
  color: inherit;
`;

const TITLE_CONTAINER_STYLE = `
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LIST_ITEM_SELECTOR = '.MuiListItem-root';
/** Create a `div` element. Centralizes the tag literal so it isn't repeated. */
function createDiv(): HTMLDivElement {
  return document.createElement('div');
}

/** Resolve the popup's title text via its sibling list-item button. */
function resolvePopupTitle(paperEl: Element): string {
  const button = paperEl
    .closest(LIST_ITEM_SELECTOR)
    ?.querySelector('.MuiListItemButton-root');
  const titleElement = button?.querySelector('.MuiTypography-root');
  return titleElement?.textContent ?? '';
}

/** Build the header node (icon + title) for a popup. */
function buildHeader(paperEl: Element, title: string): HTMLDivElement {
  const header = createDiv();
  header.className = 'popup-header';
  header.style.cssText = HEADER_STYLE;

  const iconContainer = createDiv();
  iconContainer.style.cssText = ICON_CONTAINER_STYLE;

  const iconElement = paperEl
    .closest(LIST_ITEM_SELECTOR)
    ?.querySelector('.MuiSvgIcon-root, .MuiListItemIcon-root svg');
  if (iconElement) {
    const clonedIcon = iconElement.cloneNode(true) as HTMLElement;
    clonedIcon.style.cssText = CLONED_ICON_STYLE;
    iconContainer.appendChild(clonedIcon);
  }

  const titleContainer = createDiv();
  titleContainer.style.cssText = TITLE_CONTAINER_STYLE;
  titleContainer.textContent = title;

  header.appendChild(iconContainer);
  header.appendChild(titleContainer);
  return header;
}

/** Inject a title header into a single popup paper element, if eligible. */
function decoratePopup(paperEl: Element): void {
  // Already decorated.
  if (paperEl.querySelector('.popup-header')) {
    return;
  }
  const title = resolvePopupTitle(paperEl);
  if (!title) {
    return;
  }
  const listRoot = paperEl.querySelector('.MuiList-root');
  if (!listRoot) {
    return;
  }
  paperEl.insertBefore(buildHeader(paperEl, title), listRoot);
}

/**
 * Hook to add title headers to popup menus for NavNode items
 * This ensures navigation popups have proper headers with icons and titles
 */
export const useTitleHeaders = (navigationSignature: string) => {
  useEffect(() => {
    const addPopupHeaders = () => {
      document
        .querySelectorAll(
          '.MuiDrawer-root .MuiListItem-root > .MuiBox-root .MuiPaper-root'
        )
        .forEach(decoratePopup);
    };

    // Run immediately
    addPopupHeaders();

    // Set up observer to watch for new popups
    const observer = new MutationObserver(() => {
      addPopupHeaders();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [navigationSignature]);
};
